import { useState } from 'react';
import {
  Popover,
  Box,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslate } from 'react-admin';

// Common reaction emojis (WhatsApp-style quick reactions)
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// Extended emoji categories for full picker
const EMOJI_CATEGORIES = {
  recent: [] as string[], // Would be populated from localStorage in a real app
  smileys: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😋', '😛', '😜', '🤪', '😝', '🤗', '🤭', '🤫', '🤔', '🤐', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖'],
  gestures: ['👍', '👎', '👊', '✊', '🤛', '🤜', '🤝', '👏', '🙌', '👐', '🤲', '🙏', '✍️', '💪', '🦾', '🖕', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖐', '✋', '👋', '🤚', '👌', '🤌', '🤏', '✨'],
  hearts: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟'],
  objects: ['🎉', '🎊', '🎁', '🏆', '🥇', '⭐', '🌟', '✨', '💫', '🔥', '💯', '✅', '❌', '⚠️', '❓', '❗', '💬', '💭', '🗯️', '📌', '📍', '🔔', '🔕'],
};

interface EmojiReactionPickerProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelect: (emoji: string) => void;
  isLoading?: boolean;
  /** Current user's reaction on this message (to show as selected) */
  currentReaction?: string | null;
  /** Keep picker open after selecting an emoji (useful for text insertion) */
  keepOpenOnSelect?: boolean;
}

/**
 * Emoji reaction picker with quick reactions and extended emoji panel.
 * Follows WhatsApp-style quick reaction bar with option to expand.
 */
export function EmojiReactionPicker({
  anchorEl,
  open,
  onClose,
  onSelect,
  isLoading = false,
  currentReaction,
  keepOpenOnSelect = false,
}: EmojiReactionPickerProps) {
  const translate = useTranslate();
  const [showFullPicker, setShowFullPicker] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<keyof typeof EMOJI_CATEGORIES>('smileys');

  const handleEmojiClick = (emoji: string) => {
    if (isLoading) return;
    onSelect(emoji);
    if (!keepOpenOnSelect) {
      onClose();
      setShowFullPicker(false);
    }
  };

  const handleRemoveReaction = () => {
    if (isLoading) return;
    onSelect(''); // Empty emoji means remove reaction
    onClose();
    setShowFullPicker(false);
  };

  const handleClose = () => {
    onClose();
    setShowFullPicker(false);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            overflow: 'hidden',
          },
        },
      }}
    >
      {isLoading ? (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      ) : showFullPicker ? (
        // Full emoji picker
        <Box sx={{ width: 320 }}>
          {/* Category tabs */}
          <Box
            sx={{
              display: 'flex',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 1,
              py: 0.5,
            }}
          >
            {Object.keys(EMOJI_CATEGORIES).map((category) => {
              const catKey = category as keyof typeof EMOJI_CATEGORIES;
              const categoryEmojis = EMOJI_CATEGORIES[catKey];
              if (categoryEmojis.length === 0) return null;

              return (
                <IconButton
                  key={category}
                  size="small"
                  onClick={() => setSelectedCategory(catKey)}
                  sx={{
                    opacity: selectedCategory === category ? 1 : 0.5,
                    fontSize: 18,
                  }}
                >
                  {categoryEmojis[0]}
                </IconButton>
              );
            })}
          </Box>

          {/* Emoji grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: 0.25,
              p: 1,
              maxHeight: 200,
              overflowY: 'auto',
            }}
          >
            {EMOJI_CATEGORIES[selectedCategory].map((emoji) => (
              <IconButton
                key={emoji}
                size="small"
                onClick={() => handleEmojiClick(emoji)}
                sx={{
                  fontSize: 20,
                  p: 0.5,
                  borderRadius: 1,
                  bgcolor: currentReaction === emoji ? 'action.selected' : 'transparent',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {emoji}
              </IconButton>
            ))}
          </Box>

          {/* Back to quick reactions */}
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1,
            }}
          >
            <Typography
              variant="caption"
              color="primary"
              sx={{ cursor: 'pointer' }}
              onClick={() => setShowFullPicker(false)}
            >
              {translate('resources.message.reactions.backToQuick')}
            </Typography>
          </Box>
        </Box>
      ) : (
        // Quick reactions bar
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.75,
          }}
        >
          {QUICK_REACTIONS.map((emoji) => (
            <Tooltip key={emoji} title={emoji} placement="top">
              <IconButton
                size="small"
                onClick={() => handleEmojiClick(emoji)}
                sx={{
                  fontSize: 22,
                  p: 0.75,
                  borderRadius: '50%',
                  bgcolor: currentReaction === emoji ? 'action.selected' : 'transparent',
                  transition: 'transform 0.1s',
                  '&:hover': {
                    transform: 'scale(1.2)',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {emoji}
              </IconButton>
            </Tooltip>
          ))}

          {/* Expand to full picker */}
          <Tooltip
            title={translate('resources.message.reactions.moreEmojis')}
            placement="top"
          >
            <IconButton
              size="small"
              onClick={() => setShowFullPicker(true)}
              sx={{
                fontSize: 18,
                p: 0.75,
                ml: 0.5,
                color: 'text.secondary',
              }}
            >
              ➕
            </IconButton>
          </Tooltip>

          {/* Remove reaction (if user has reacted) */}
          {currentReaction && (
            <Tooltip
              title={translate('resources.message.reactions.remove')}
              placement="top"
            >
              <IconButton
                size="small"
                onClick={handleRemoveReaction}
                sx={{
                  fontSize: 14,
                  p: 0.75,
                  ml: 0.5,
                  color: 'error.main',
                }}
              >
                ✕
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Popover>
  );
}
